import React from 'react';
import { Box, Card, Skeleton, alpha } from '@mui/material';

const SaturationDashboardSkeleton = () => {
  return (
    <Box sx={{ width: '100%', px: 0, py: { xs: 1, sm: 2 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
        <Box>
          <Skeleton variant="text" width={240} height={36} animation="wave" />
          <Skeleton variant="text" width={300} height={20} animation="wave" />
        </Box>
        <Skeleton variant="rounded" width={110} height={40} animation="wave" sx={{ borderRadius: 1 }} />
      </Box>

      {/* Key Metrics Cards */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
        gap: { xs: 1.5, md: 2 },
        mb: { xs: 2, md: 2.5 },
      }}>
        {[1, 2, 3, 4].map(i => (
          <Box
            key={i}
            sx={{
              p: 2.5,
              borderRadius: 2.5,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            <Skeleton variant="text" width="55%" height={14} animation="wave" sx={{ mb: 1 }} />
            <Skeleton variant="text" width="40%" height={32} animation="wave" sx={{ mb: 0.5 }} />
            <Skeleton variant="text" width="70%" height={14} animation="wave" />
          </Box>
        ))}
      </Box>

      {/* Main two-column */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, md: 2.5 } }}>
        {/* Left: Sector Rankings */}
          <Box sx={{ flex: { xs: '1 1 100%', lg: '1 1 calc(58% - 10px)' }, minWidth: 0 }}>
          <Card
            elevation={0}
            sx={{ borderRadius: 2.5, border: '1px solid', borderColor: 'divider', p: { xs: 2, sm: 3 } }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
              <Box>
                <Skeleton variant="text" width={200} height={26} animation="wave" />
                <Skeleton variant="text" width={160} height={16} animation="wave" />
              </Box>
              <Skeleton variant="rounded" width={64} height={30} animation="wave" sx={{ borderRadius: 1 }} />
            </Box>

            {[...Array(11)].map((_, i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: 1, sm: 1.25 },
                  py: 1,
                  px: 1.25,
                  borderRadius: 1.5,
                }}
              >
                <Skeleton variant="circular" width={8} height={8} animation="wave" />
                <Skeleton variant="text" width={120} height={18} animation="wave" sx={{ flexShrink: 0 }} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton
                    variant="rounded"
                    width={`${65 - i * 4}%`}
                    height={7}
                    animation="wave"
                    sx={{
                      borderRadius: 5,
                      bgcolor: alpha(
                        i < 3 ? '#f57c00' : i < 7 ? '#fbc02d' : '#388e3c',
                        0.12
                      ),
                    }}
                  />
                </Box>
                <Skeleton variant="text" width={36} height={18} animation="wave" />
                <Skeleton
                  variant="rounded"
                  width={55}
                  height={21}
                  animation="wave"
                  sx={{ borderRadius: 11, display: { xs: 'none', sm: 'block' } }}
                />
              </Box>
            ))}
          </Card>
        </Box>

        {/* Right: Distribution + Guide */}
        <Box
          sx={{
            flex: { xs: '1 1 100%', lg: '1 1 calc(42% - 10px)' },
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: 2, md: 2 },
          }}
        >
          {/* Donut skeleton */}
          <Card elevation={0} sx={{ borderRadius: 2.5, border: '1px solid', borderColor: 'divider', p: { xs: 2, sm: 2.5 } }}>
            <Skeleton variant="text" width={180} height={26} animation="wave" sx={{ mb: 1.5 }} />
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <Skeleton
                variant="circular"
                width={152}
                height={152}
                animation="wave"
                sx={{ bgcolor: alpha('#667eea', 0.06) }}
              />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 1 }}>
              {[1, 2, 3].map(i => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Skeleton variant="circular" width={8} height={8} animation="wave" />
                  <Skeleton variant="text" width={50} height={14} animation="wave" />
                </Box>
              ))}
            </Box>
          </Card>

          {/* Guide skeleton */}
          <Card elevation={0} sx={{ borderRadius: 2.5, border: '1px solid', borderColor: 'divider', p: { xs: 2, sm: 2.5 }, flex: 1 }}>
            <Skeleton variant="text" width={160} height={26} animation="wave" sx={{ mb: 1.5 }} />
            {[1, 2, 3, 4].map(i => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  py: 0.75,
                  px: 1,
                  mb: 0.5,
                  borderRadius: 1,
                  bgcolor: alpha('#667eea', 0.03),
                }}
              >
                <Skeleton variant="circular" width={7} height={7} animation="wave" sx={{ flexShrink: 0 }} />
                <Skeleton variant="text" width="30%" height={16} animation="wave" />
                <Skeleton variant="text" width="15%" height={12} animation="wave" />
                <Box sx={{ flex: 1 }} />
                <Skeleton variant="text" width="20%" height={14} animation="wave" />
              </Box>
            ))}
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default SaturationDashboardSkeleton;
